/**
 * Sticky "acting as" banner shown whenever the session is an active
 * impersonation. Always visible so an operator can never forget they're
 * acting as someone else, with a live elapsed timer and a one-click Exit
 * that restores their own super-admin session. Paired with
 * <ImpersonationFrame/>, which rings the whole viewport for the duration.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { LogOut } from "lucide-react";

import { getActiveAccessToken, getImpersonationStartedAt } from "@/lib/auth";
import { useMe } from "@/lib/me";
import { stopImpersonation } from "@/lib/platform";

/** Epoch-ms of a JWT's `iat` claim, or null. Fallback for the elapsed timer
 *  when no client-recorded start time exists (session predates that field). */
function tokenIssuedAtMs(token: string | null): number | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    b64 += "=".repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(b64)) as { iat?: number };
    return typeof payload.iat === "number" ? payload.iat * 1000 : null;
  } catch {
    return null;
  }
}

function formatElapsed(ms: number): string {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export function ImpersonationBanner() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [stopping, setStopping] = useState(false);

  // Prefer the client-recorded start (no clock-skew); fall back to token iat.
  // Plain expression: a stable number across renders, so the timer effect
  // below doesn't re-arm. Null when not impersonating.
  const startMs = me?.impersonation
    ? getImpersonationStartedAt() ?? tokenIssuedAtMs(getActiveAccessToken())
    : null;
  const [elapsed, setElapsed] = useState(() =>
    startMs != null ? Math.max(0, Date.now() - startMs) : 0,
  );

  useEffect(() => {
    if (startMs == null) return;
    const tick = () => setElapsed(Math.max(0, Date.now() - startMs));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [startMs]);

  if (!me?.impersonation) return null;

  async function handleStop() {
    setStopping(true);
    await stopImpersonation(qc, me?.impersonation?.session_id);
    navigate("/admin/clients");
  }

  const subject = me.company?.display_name || me.display_name || me.username;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-amber-600/40 bg-gradient-to-r from-amber-400 to-amber-500 px-4 py-2 text-amber-950">
      {/* Live pulse + subject */}
      <span className="flex items-center gap-2 text-sm">
        <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-600/70" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-600" />
        </span>
        <span className="font-semibold">
          Acting as <strong className="font-bold">{subject}</strong>
        </span>
      </span>

      {/* Attribution — dropped first on narrow widths */}
      <span className="hidden text-xs text-amber-900/90 sm:inline">
        Changes are attributed to {me.impersonation.impersonator_name}.
      </span>

      {/* Timer + exit, pinned right */}
      <span className="ml-auto flex items-center gap-2.5">
        {startMs != null && (
          <span
            className="rounded-md bg-amber-950/10 px-2 py-1 font-mono text-xs font-semibold tabular-nums text-amber-950"
            title="Time in this impersonation session"
          >
            {formatElapsed(elapsed)}
          </span>
        )}
        <button
          type="button"
          onClick={handleStop}
          disabled={stopping}
          className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1 text-xs font-bold text-white shadow-sm transition-colors hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-900/40 disabled:opacity-60"
          aria-label="Exit impersonation session and return to your admin account"
        >
          <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
          {stopping ? "Exiting…" : "Exit session"}
        </button>
      </span>
    </div>
  );
}
